/** Shared types for every widget kind renderer. */

export type WidgetKind =
  | "bars"
  | "hbars"
  | "area"
  | "sparkline"
  | "line"
  | "scatter"
  | "gauge"
  | "donut"
  | "kpi"
  | "table"
  | "markdown"
  | "actions";

export interface WidgetParameter {
  name: string;
  type: "date" | "datetime" | "string" | "number" | "integer";
  default?: unknown;
  /** When present, FilterBar renders a Select. The lookup SQL is sent to
   *  POST /v1/widgets/lookup which returns {options: [{label, value}]}. */
  lookup_sql?: string;
  lookup_label_col?: string;
  lookup_value_col?: string;
  /** Multi-select. Binds as an array on the backend; SQL template must
   *  use `WHERE col = ANY($N)` instead of `WHERE col = $N`. */
  multi?: boolean;
}

export interface Widget {
  id: string;
  user_id: number;
  company_id: number;
  kind: WidgetKind;
  title: string | null;
  config: Record<string, unknown>;
  sql_template: string | null;
  parameters: WidgetParameter[];
  data: { points?: Array<Record<string, unknown>>; error?: string } | null;
  position: number;
  created_at: string;
  updated_at: string;
}

/** Shared config keys every data-driven kind honors. */
export interface BaseChartConfig {
  x_label?: string;
  y_label?: string;
  color?: string;
  per_bar_colors?: string[];
  highlight_index?: number;
  muted_color?: string;
  target_line?: number;
  format?: "money" | "pct" | "number" | "k" | "m";
}

/** Format a number per the config.format hint. */
export function formatValue(
  v: unknown,
  fmt: BaseChartConfig["format"] | undefined,
): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (fmt === "money") {
    return n.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  }
  if (fmt === "pct") return `${n.toFixed(1)}%`;
  if (fmt === "k") return `${(n / 1_000).toFixed(1)}K`;
  if (fmt === "m") return `${(n / 1_000_000).toFixed(2)}M`;
  return n.toLocaleString("en-US");
}

/** Pick per-point colors: per_bar_colors > highlight_index > color > palette. */
const PALETTE = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
];
export function pickPointColor(
  index: number,
  total: number,
  cfg: BaseChartConfig,
): string {
  if (cfg.per_bar_colors && cfg.per_bar_colors[index]) {
    return cfg.per_bar_colors[index];
  }
  if (cfg.highlight_index !== undefined) {
    return index === cfg.highlight_index
      ? cfg.color ?? PALETTE[0]
      : cfg.muted_color ?? "#1f2937";
  }
  if (cfg.color) return cfg.color;
  return PALETTE[index % PALETTE.length];
}

/** Default series color (single-series charts). */
export function pickSeriesColor(cfg: BaseChartConfig): string {
  return cfg.color ?? PALETTE[0];
}
